/**
 * The surfaces, drawn rather than downloaded.
 *
 * A kitchen rendered in flat colour reads as a diagram. Three surfaces do almost all of the
 * work of making it read as a room instead, and none of them is the cabinetry: the FLOOR,
 * which is the biggest thing in shot and was one beige rectangle; the BENCHTOP, which is the
 * surface a person looks at hardest and was one grey rectangle; and the WALL behind it. Give
 * those three a real material and the same cabinets look photographed.
 *
 * EVERY TEXTURE HERE IS DRAWN INTO A CANVAS AT RUNTIME. No image files, for the same reason
 * build.js has no model library: a downloaded texture is somebody else's licence and it does
 * not come in this maker's colours. A floor drawn from `{colour, grain, plank}` comes in
 * whatever colours the maker sells, and adding one is a line of data.
 *
 * TWO THINGS THAT MUST BE RIGHT, and both are about SIZE.
 *
 *   A texture is drawn to cover a stated number of REAL METRES -- `span` -- and the caller
 *   scales UVs so a floorboard is 180mm wide on the floor and a grout line is 3mm wide on
 *   the splashback. A tiled texture with no real size is wallpaper, and it is the thing that
 *   makes a render look like a video game.
 *
 *   A colour map is sRGB and a roughness map is NOT. Getting that backwards is not subtle:
 *   the roughness comes out gamma-shifted and every gloss door goes flat.
 *
 * Deterministic: one seeded PRNG per texture, so the same kitchen renders the same twice and
 * a still taken for the report matches what was on screen.
 *
 * Cached by key. A material rebuild on every finish change would otherwise allocate a new
 * megabyte canvas each time somebody taps a colour.
 */
import * as THREE from 'three'

const cache = new Map()
let aniso = 8

/** Told once, by the scene, from the renderer's own capabilities. */
export function setAnisotropy(n) {
  const v = Math.max(1, Math.min(16, Math.floor(n) || 1))
  if (v === aniso) return
  aniso = v
  for (const t of cache.values()) {
    for (const k of ['map', 'roughnessMap']) if (t[k]) t[k].anisotropy = aniso
  }
}

/** Everything drawn here is thrown away with the scene. */
export function disposeTextures() {
  for (const t of cache.values()) {
    for (const k of ['map', 'roughnessMap']) t[k]?.dispose()
  }
  cache.clear()
}

/**
 * A canvas, or null where there is no DOM.
 *
 * The tests run in node and the pure geometry has to keep working there, so every caller
 * treats a null texture as "use the flat colour" rather than as a failure.
 */
function surface(px) {
  if (typeof document === 'undefined') return null
  const c = document.createElement('canvas')
  c.width = c.height = px
  return c
}

/** A small deterministic PRNG. Same kitchen, same grain, every time. */
function rng(seed) {
  let s = seed >>> 0 || 1
  return () => {
    s ^= s << 13; s >>>= 0
    s ^= s >> 17
    s ^= s << 5; s >>>= 0
    return s / 4294967296
  }
}

const seedOf = (s) => {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) }
  return h >>> 0
}

function texFrom(canvas, { srgb }) {
  const t = new THREE.CanvasTexture(canvas)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  t.anisotropy = aniso
  return t
}

/** Cache, and hand back the same textures for the same finish. */
function made(key, build) {
  if (cache.has(key)) return cache.get(key)
  const v = build() ?? { map: null, roughnessMap: null, span: 1 }
  cache.set(key, v)
  return v
}

// ── colour helpers ────────────────────────────────────────────────────────────────────

/** #rrggbb to [r,g,b]. */
function rgb(hex) {
  const h = String(hex).replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}
/** A colour moved toward white (d > 0) or black (d < 0), in 0-255 steps. */
function shift(hex, d) {
  const c = rgb(hex).map((v) => Math.max(0, Math.min(255, Math.round(v + d))))
  return `rgb(${c[0]},${c[1]},${c[2]})`
}
const grey = (v) => `rgb(${v},${v},${v})`

// ── floors ────────────────────────────────────────────────────────────────────────────

/**
 * A floor.
 *
 * `kind` is what it is made of and decides how it is drawn:
 *   boards    planks of `plank: [width, length]` in mm, staggered, each its own tone
 *   tile      a grid of `tile: [w, h]` in mm with a grout line between
 *   concrete  no joints at all, just cloud and aggregate
 *
 * @returns {{map, roughnessMap, span}} span is how many real metres the texture covers.
 */
export function floorTexture(spec) {
  return made(`floor:${spec.id}`, () => {
    const span = spec.kind === 'boards' ? Math.max(2.4, (spec.plank?.[1] ?? 1200) / 1000 * 2)
      : spec.kind === 'tile' ? Math.max(1.2, (spec.tile?.[0] ?? 600) / 1000 * 2)
        : 2.4
    const px = 1024
    const c = surface(px), r = surface(px)
    if (!c || !r) return null
    const g = c.getContext('2d'), gr = r.getContext('2d')
    const rand = rng(seedOf(spec.id))
    const per = px / span            // pixels per metre

    g.fillStyle = spec.colour; g.fillRect(0, 0, px, px)
    // Rough by default; the joints and the darker grain are rougher still, which is what
    // stops a floor reading as one sheet of plastic under a moving light.
    gr.fillStyle = grey(Math.round(200 - (spec.gloss ?? 0.2) * 150)); gr.fillRect(0, 0, px, px)

    if (spec.kind === 'boards') boards(g, gr, spec, { px, per, rand })
    else if (spec.kind === 'tile') tiles(g, gr, spec, { px, per, rand })
    else concrete(g, gr, spec, { px, rand })

    return { map: texFrom(c, { srgb: true }), roughnessMap: texFrom(r, { srgb: false }), span }
  })
}

function boards(g, gr, spec, { px, per, rand }) {
  const w = (spec.plank?.[0] ?? 180) / 1000 * per
  const len = (spec.plank?.[1] ?? 1200) / 1000 * per
  const rows = Math.max(1, Math.round(px / w))
  const step = px / rows

  for (let i = 0; i < rows; i++) {
    const y = i * step
    // Every board its own tone: a floor of identical boards is a floor nobody has ever laid.
    g.fillStyle = shift(spec.colour, (rand() - 0.5) * 34)
    g.fillRect(0, y, px, step)

    // The grain, along the board.
    for (let k = 0; k < 26; k++) {
      const gy = y + rand() * step
      g.strokeStyle = shift(spec.grain ?? spec.colour, (rand() - 0.5) * 30)
      g.globalAlpha = 0.10 + rand() * 0.22
      g.lineWidth = 0.6 + rand() * 1.8
      g.beginPath()
      g.moveTo(0, gy)
      // A slow wander, so the grain is not a ruled line.
      for (let x = 0; x <= px; x += 48) g.lineTo(x, gy + Math.sin((x / px) * 6 + k) * step * 0.06)
      g.stroke()
    }
    g.globalAlpha = 1

    // The joint between boards: a dark line, and a lighter one under it for the bevel.
    g.strokeStyle = shift(spec.grain ?? spec.colour, -58); g.lineWidth = 1.8
    g.beginPath(); g.moveTo(0, y); g.lineTo(px, y); g.stroke()
    gr.strokeStyle = grey(238); gr.lineWidth = 2.4
    gr.beginPath(); gr.moveTo(0, y); gr.lineTo(px, y); gr.stroke()

    // End joints, staggered by row so the floor does not band.
    let x = (i * len * 0.37) % len
    while (x < px) {
      g.strokeStyle = shift(spec.grain ?? spec.colour, -52); g.lineWidth = 1.4
      g.beginPath(); g.moveTo(x, y); g.lineTo(x, y + step); g.stroke()
      gr.strokeStyle = grey(238); gr.lineWidth = 2
      gr.beginPath(); gr.moveTo(x, y); gr.lineTo(x, y + step); gr.stroke()
      x += len
    }

    // A wear pattern down the length, so the roughness is not flat either.
    gr.globalAlpha = 0.35
    gr.fillStyle = grey(Math.round(190 + rand() * 40))
    gr.fillRect(0, y + step * 0.2, px, step * 0.55)
    gr.globalAlpha = 1
  }
}

function tiles(g, gr, spec, { px, per, rand }) {
  const tw = (spec.tile?.[0] ?? 600) / 1000 * per
  const th = (spec.tile?.[1] ?? spec.tile?.[0] ?? 600) / 1000 * per
  const cols = Math.max(1, Math.round(px / tw)), rows = Math.max(1, Math.round(px / th))
  const cw = px / cols, ch = px / rows
  const joint = Math.max(1.5, (spec.joint ?? 3) / 1000 * per)

  g.fillStyle = spec.grout ?? shift(spec.colour, -22); g.fillRect(0, 0, px, px)
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const x = i * cw + joint / 2, y = j * ch + joint / 2
      const w = cw - joint, h = ch - joint
      g.fillStyle = shift(spec.colour, (rand() - 0.5) * 14)
      g.fillRect(x, y, w, h)
      // Cloud in the face of the tile: stone is never one colour across 600mm.
      for (let k = 0; k < 14; k++) {
        g.globalAlpha = 0.05 + rand() * 0.08
        g.fillStyle = shift(spec.colour, (rand() - 0.5) * 50)
        const rx = x + rand() * w, ry = y + rand() * h
        g.beginPath()
        g.ellipse(rx, ry, w * (0.06 + rand() * 0.2), h * (0.05 + rand() * 0.16), rand() * 3, 0, Math.PI * 2)
        g.fill()
      }
      g.globalAlpha = 1
      // Grout is matt whatever the tile is; that contrast is most of what says "tiled".
      gr.fillStyle = grey(250)
      gr.fillRect(i * cw, j * ch, cw, ch)
      gr.fillStyle = grey(Math.round(190 - (spec.gloss ?? 0.2) * 130))
      gr.fillRect(x, y, w, h)
    }
  }
}

function concrete(g, gr, spec, { px, rand }) {
  for (let k = 0; k < 260; k++) {
    g.globalAlpha = 0.03 + rand() * 0.07
    g.fillStyle = shift(spec.colour, (rand() - 0.5) * 46)
    const x = rand() * px, y = rand() * px, s = px * (0.03 + rand() * 0.16)
    g.beginPath(); g.ellipse(x, y, s, s * (0.5 + rand()), rand() * 3, 0, Math.PI * 2); g.fill()
  }
  // Aggregate: the small hard flecks a polished slab shows where it has been ground back.
  for (let k = 0; k < 1400; k++) {
    g.globalAlpha = 0.10 + rand() * 0.3
    g.fillStyle = shift(spec.colour, rand() > 0.5 ? 40 : -40)
    const x = rand() * px, y = rand() * px, s = 0.6 + rand() * 2.4
    g.beginPath(); g.arc(x, y, s, 0, Math.PI * 2); g.fill()
    gr.globalAlpha = 0.4
    gr.fillStyle = grey(Math.round(60 + rand() * 120))
    gr.beginPath(); gr.arc(x, y, s * 1.4, 0, Math.PI * 2); gr.fill()
  }
  g.globalAlpha = gr.globalAlpha = 1
}

// ── walls ─────────────────────────────────────────────────────────────────────────────

/**
 * Plaster: no colour map at all, only a roughness one.
 *
 * A painted wall IS its colour -- putting a colour map on it would fight the paint the
 * person chose. What it is not is perfectly smooth, and a wall with no variation at all
 * catches the directional light as one clean gradient, which is the single most
 * computer-looking thing in the room.
 */
export function wallTexture() {
  return made('wall:plaster', () => {
    const px = 512
    const r = surface(px)
    if (!r) return null
    const gr = r.getContext('2d')
    const rand = rng(seedOf('plaster'))
    gr.fillStyle = grey(242); gr.fillRect(0, 0, px, px)
    for (let k = 0; k < 900; k++) {
      gr.globalAlpha = 0.05 + rand() * 0.12
      gr.fillStyle = grey(Math.round(216 + rand() * 34))
      const x = rand() * px, y = rand() * px, s = 2 + rand() * 26
      gr.beginPath(); gr.ellipse(x, y, s, s * (0.4 + rand()), rand() * 3, 0, Math.PI * 2); gr.fill()
    }
    gr.globalAlpha = 1
    return { map: null, roughnessMap: texFrom(r, { srgb: false }), span: 1.6 }
  })
}

// ── stone, for benchtops and splashbacks ──────────────────────────────────────────────

/**
 * A benchtop.
 *
 * `fleck` is already in the maker's data and was only ever used for the little swatch in the
 * picker. Here it does the real job: it is the second colour in the stone. `veined` draws
 * marble's veins over the top of it, which is the whole difference between Carrara and a
 * grey rectangle, and `timber` makes it a solid oak top instead.
 */
export function stoneTexture(spec) {
  return made(`stone:${spec.id}`, () => {
    const span = 1.2
    const px = 1024
    const c = surface(px), r = surface(px)
    if (!c || !r) return null
    const g = c.getContext('2d'), gr = r.getContext('2d')
    const rand = rng(seedOf(spec.id))

    g.fillStyle = spec.colour; g.fillRect(0, 0, px, px)
    gr.fillStyle = grey(Math.round(210 - (spec.gloss ?? 0.5) * 170)); gr.fillRect(0, 0, px, px)

    if (spec.timber) {
      for (let k = 0; k < 220; k++) {
        const y = rand() * px
        g.strokeStyle = shift(spec.fleck ?? spec.colour, (rand() - 0.5) * 40)
        g.globalAlpha = 0.08 + rand() * 0.2
        g.lineWidth = 0.6 + rand() * 2.2
        g.beginPath(); g.moveTo(0, y)
        for (let x = 0; x <= px; x += 40) g.lineTo(x, y + Math.sin(x / 90 + k) * 3)
        g.stroke()
      }
      g.globalAlpha = 1
    } else {
      // The fleck: the mineral scatter that makes engineered stone look like stone.
      for (let k = 0; k < 5200; k++) {
        g.globalAlpha = 0.10 + rand() * 0.42
        g.fillStyle = shift(spec.fleck ?? spec.colour, (rand() - 0.5) * 44)
        g.beginPath(); g.arc(rand() * px, rand() * px, 0.5 + rand() * 2.1, 0, Math.PI * 2); g.fill()
      }
      g.globalAlpha = 1
      if (spec.veined) veins(g, gr, spec, { px, rand })
    }
    return { map: texFrom(c, { srgb: true }), roughnessMap: texFrom(r, { srgb: false }), span }
  })
}

/** Marble veining: a few big wandering rivers, each with hairlines running beside it. */
function veins(g, gr, spec, { px, rand }) {
  for (let v = 0; v < 7; v++) {
    const y0 = rand() * px * 1.4 - px * 0.2
    const drift = (rand() - 0.5) * px * 0.6
    const draw = (w, alpha, colour) => {
      g.strokeStyle = colour; g.globalAlpha = alpha; g.lineWidth = w
      g.beginPath(); g.moveTo(-20, y0)
      let y = y0
      for (let x = -20; x <= px + 20; x += 26) {
        y += (rand() - 0.5) * 14 + drift / (px / 26)
        g.lineTo(x, y)
      }
      g.stroke()
      // The vein is a different mineral, so it takes the polish differently.
      gr.strokeStyle = grey(Math.round(120 + rand() * 60)); gr.globalAlpha = alpha * 0.7
      gr.lineWidth = w * 1.6; gr.stroke()
    }
    draw(2 + rand() * 5, 0.22 + rand() * 0.2, shift(spec.fleck ?? spec.colour, -30))
    for (let h = 0; h < 3; h++) draw(0.7 + rand(), 0.10 + rand() * 0.12, shift(spec.fleck ?? spec.colour, -10))
  }
  g.globalAlpha = gr.globalAlpha = 1
}

/**
 * A splashback that is TILED, drawn at the real tile size with a real grout line.
 *
 * A glass or stone splashback is a sheet and gets nothing -- it is one surface, and drawing
 * joints into it would be a lie about what the maker is supplying.
 */
export function splashTexture(spec) {
  if (!spec?.tile) return { map: null, roughnessMap: null, span: 1 }
  return made(`splash:${spec.id}`, () => {
    const span = 0.6
    const px = 1024
    const c = surface(px), r = surface(px)
    if (!c || !r) return null
    const per = px / span
    tiles(c.getContext('2d'), r.getContext('2d'), spec, { px, per, rand: rng(seedOf(spec.id)) })
    return { map: texFrom(c, { srgb: true }), roughnessMap: texFrom(r, { srgb: false }), span }
  })
}

// ── door faces ────────────────────────────────────────────────────────────────────────

/**
 * What a door is MADE of, as opposed to what colour it is.
 *
 * Two jobs. A timber colour gets grain, which is the only way "Natural Oak" reads as oak
 * rather than as beige. And every other door gets a very slight roughness variation, because
 * a 600 x 720 panel of perfectly uniform roughness under an environment map is the surface
 * that most reliably says "rendered": the difference between a matt laminate and a two-pack
 * gloss is what somebody is choosing between, and it only shows if the highlight moves.
 */
export function doorTexture(spec) {
  return made(`door:${spec.id}`, () => {
    const px = 512
    const wood = !!spec.grain
    const c = wood ? surface(px) : null
    const r = surface(px)
    if (!r || (wood && !c)) return null
    const gr = r.getContext('2d')
    const rand = rng(seedOf(spec.id))
    const gloss = spec.gloss ?? 0.15

    gr.fillStyle = grey(Math.round(245 - gloss * 210)); gr.fillRect(0, 0, px, px)
    for (let k = 0; k < 420; k++) {
      gr.globalAlpha = 0.05 + rand() * 0.10
      gr.fillStyle = grey(Math.round(120 + rand() * 130))
      const x = rand() * px, y = rand() * px, s = 3 + rand() * 40
      gr.beginPath(); gr.ellipse(x, y, s * 0.3, s, rand() * 0.4, 0, Math.PI * 2); gr.fill()
    }
    gr.globalAlpha = 1

    if (!wood) return { map: null, roughnessMap: texFrom(r, { srgb: false }), span: 0.6 }

    // Grain runs UP a door, which is how a door is cut, so it is drawn vertically here and
    // the geometry's own UVs are left alone.
    const g = c.getContext('2d')
    g.fillStyle = spec.colour; g.fillRect(0, 0, px, px)
    for (let k = 0; k < 200; k++) {
      const x = rand() * px
      g.strokeStyle = shift(spec.colour, (rand() - 0.5) * 46)
      g.globalAlpha = 0.08 + rand() * 0.22
      g.lineWidth = 0.6 + rand() * 2.4
      g.beginPath(); g.moveTo(x, 0)
      for (let y = 0; y <= px; y += 32) g.lineTo(x + Math.sin(y / 110 + k) * 4, y)
      g.stroke()
    }
    g.globalAlpha = 1
    return { map: texFrom(c, { srgb: true }), roughnessMap: texFrom(r, { srgb: false }), span: 0.6 }
  })
}
